import * as React from 'react';
import type { ResearchResultData } from 'shared/src/api/ai-media-plan-chat-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faChevronDown,
  faChevronRight,
  faArrowUpRightFromSquare
} from '@fortawesome/pro-light-svg-icons';
import { formatEntityTypeLabel } from './research-labels';
import { InfoPanelFrame } from '@/features/ai/shared/info-panel-frame';

type Props = {
  data: ResearchResultData;
  mediaBuyEntityType: string;
  platformShortLabel: string;
};

export function ResearchResultsCard({ data, mediaBuyEntityType, platformShortLabel }: Props) {
  const [sourceExpanded, setSourceExpanded] = React.useState(false);
  const [rulesExpanded, setRulesExpanded] = React.useState(false);
  const { specified, research, details } = data;

  const specifiedFields = deduplicateSpecified(specified);

  return (
    // Agent-emitted (the flow asked for this research), so it arrives expanded; the render site
    // keys the card on the emitting part so only a NEW research run re-opens it.
    <InfoPanelFrame
      title="Targeting Research"
      detail={`${research.patterns_found} patterns from ${research.line_items_searched} ${formatEntityTypeLabel(mediaBuyEntityType, research.line_items_searched)}`}
      defaultOpen>
      {/* Section 1: Specified Targeting (locked) */}
      <div className="mb-3 rounded-lg bg-gray-50 p-3">
        <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-600">
          <FontAwesomeIcon icon={faLock} className="h-3 w-3" />
          SPECIFIED TARGETING
        </h4>
        {specifiedFields.length > 0 ? (
          <dl className="space-y-0.5 text-xs">
            {specifiedFields.map((f, i) => (
              <div key={i} className="flex items-start gap-2">
                <dt className="shrink-0 text-gray-500">{f.label}:</dt>
                <dd className="font-medium text-gray-900">{f.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-xs text-gray-500">
            No targeting specified — research suggestions will be used as baseline
          </p>
        )}
      </div>

      {/* Section 2: Research Patterns (read-only) */}
      <div className="mb-3">
        <h4 className="mb-1.5 text-xs font-semibold text-blue-700">RESEARCH PATTERNS</h4>
        {research.patterns_found > 0 ? (
          <>
            <p className="mb-2 text-xs text-blue-600">
              {research.line_items_searched}{' '}
              {formatEntityTypeLabel(mediaBuyEntityType, research.line_items_searched)} across{' '}
              {research.campaigns_searched}{' '}
              {research.campaigns_searched === 1 ? 'campaign' : 'campaigns'}
              {research.budget_range.min > 0
                ? ` ($${research.budget_range.min.toLocaleString()}–$${research.budget_range.max.toLocaleString()})`
                : ''}
            </p>
            {research.geo_patterns.length > 0 ? (
              <div className="mb-2 flex flex-wrap gap-1">
                {research.geo_patterns.map((geo, i) => (
                  <span
                    key={`geo-${i}`}
                    className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    {geo}
                  </span>
                ))}
              </div>
            ) : null}
            {research.suggested_rules.length > 0 ? (
              <div>
                <div className="flex flex-wrap gap-1">
                  {research.suggested_rules.map((rule, i) => (
                    <span
                      key={i}
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        rule.group === 'none'
                          ? 'bg-red-50 text-red-700'
                          : rule.group === 'any'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                      {formatKey(rule.key)}{' '}
                      <span className="opacity-60">
                        ({rule.seen_in}/{research.patterns_found})
                      </span>
                    </span>
                  ))}
                </div>
                {/* Drill-down: expand to see values per rule */}
                <button
                  onClick={() => setRulesExpanded(!rulesExpanded)}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800">
                  <FontAwesomeIcon
                    icon={rulesExpanded ? faChevronDown : faChevronRight}
                    className="h-3 w-3"
                  />
                  {rulesExpanded ? 'Hide' : 'Show'} rule details
                </button>
                {rulesExpanded ? (
                  <div className="mt-2 space-y-1.5">
                    {research.suggested_rules.map((rule, i) => {
                      const hasLabels = rule.values.some(v => v.label);
                      return (
                        <div key={i} className="rounded-lg bg-white px-2.5 py-2 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900">{formatKey(rule.key)}</span>
                            <GroupBadge group={rule.group} />
                            <span className="text-[10px] text-gray-400">
                              {rule.seen_in}/{research.patterns_found}
                            </span>
                          </div>
                          {hasLabels ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {rule.values
                                .filter(v => v.label)
                                .map(v => (
                                  <span
                                    key={v.id}
                                    className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                                    {v.label}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <div className="mt-0.5 text-gray-400">
                              {rule.values.length} {rule.values.length === 1 ? 'entry' : 'entries'}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-blue-600">
            No targeting patterns found in similar line items. A baseline will be proposed.
          </p>
        )}
      </div>

      {/* Section 3: Source Items (collapsible) */}
      {details.length > 0 ? (
        <div className="border-t border-blue-200 pt-2">
          <button
            onClick={() => setSourceExpanded(!sourceExpanded)}
            className="flex w-full items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800">
            <FontAwesomeIcon
              icon={sourceExpanded ? faChevronDown : faChevronRight}
              className="h-3 w-3"
            />
            {sourceExpanded ? 'Hide' : 'Show'} {details.length} source{' '}
            {formatEntityTypeLabel(mediaBuyEntityType, details.length)}
          </button>
          {sourceExpanded ? (
            <div className="mt-2 space-y-2">
              {details.map((d, i) => (
                <SourceItemChain key={i} item={d} platformShortLabel={platformShortLabel} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </InfoPanelFrame>
  );
}

// --- Private helpers ---

type SourceItem = ResearchResultData['details'][number];

function SourceItemChain({
  item,
  platformShortLabel
}: {
  item: SourceItem;
  platformShortLabel: string;
}) {
  return (
    <div className="rounded-lg bg-white px-3 py-2 text-xs">
      <ChainRow
        label="MT"
        labelClassName="bg-sky-100 text-sky-700"
        href={`/campaigns/${item.campaign_id}/execution/lineitems/${item.line_item_id}`}
        linkTitle="Open in Media Tool">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate font-medium text-gray-900">{item.line_item_name}</span>
          <span className="shrink-0 text-gray-400">({item.campaign_name})</span>
        </div>
        <div className="mt-0.5 flex gap-3 text-gray-500">
          <span>${item.price.toLocaleString()}</span>
          <span>{item.geo}</span>
          <span className="text-gray-400">{item.line_item_type}</span>
        </div>
      </ChainRow>

      <ChainRow
        label={platformShortLabel}
        labelClassName="bg-amber-100 text-amber-700"
        href={item.parent_platform_entity_url}
        linkTitle={`Open parent in ${platformShortLabel}`}>
        {item.parent_platform_entity_id ? (
          <span className="truncate text-gray-900">{item.parent_platform_entity_name}</span>
        ) : (
          <span className="text-gray-400">— (parent not synced)</span>
        )}
      </ChainRow>

      <ChainRow
        label={platformShortLabel}
        labelClassName="bg-amber-100 text-amber-700"
        href={item.platform_entity_url}
        linkTitle={`Open in ${platformShortLabel}`}>
        <span className="truncate text-gray-900">
          {item.platform_entity_name}{' '}
          <span className="text-gray-400">(#{item.platform_entity_id})</span>
        </span>
      </ChainRow>
    </div>
  );
}

function ChainRow({
  label,
  labelClassName,
  href,
  linkTitle,
  children
}: {
  label: string;
  labelClassName: string;
  href: string | null;
  linkTitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span
        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${labelClassName}`}>
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded p-1 text-blue-500 hover:bg-blue-100 hover:text-blue-700"
          title={linkTitle}>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
        </a>
      ) : (
        <span className="shrink-0 w-6" />
      )}
    </div>
  );
}

function deduplicateSpecified(
  specified: ResearchResultData['specified']
): Array<{ label: string; value: string }> {
  const seen = new Set<string>();
  const fields: Array<{ label: string; value: string }> = [];

  const candidates = [
    { label: 'Geo', value: specified.geo },
    { label: 'Audience', value: specified.audience },
    { label: 'Targeting', value: specified.targeting },
    { label: 'CRM Geo Targets', value: specified.zoho_geo },
    { label: 'CRM Audience', value: specified.zoho_audience }
  ];

  for (const c of candidates) {
    if (!c.value) continue;
    const normalized = c.value.trim().toLowerCase();
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    fields.push({ label: c.label, value: c.value });
  }

  return fields;
}

function GroupBadge({ group }: { group: string }) {
  const config: Record<string, { label: string; className: string }> = {
    all: { label: 'Include', className: 'bg-green-100 text-green-700' },
    any: { label: 'Match Any', className: 'bg-amber-100 text-amber-700' },
    none: { label: 'Exclude', className: 'bg-red-100 text-red-700' }
  };
  const c = config[group] ?? { label: group, className: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

function formatKey(key: string): string {
  return key
    .split('.')
    .pop()!
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
